const groupPrefix = createApiRoute(ApiModule.ORDER, ApiAuthorization.EMPLOYEE, ApiDataModel.CLIENT_DIETS)

export const apiOrders = {
	fetchOrders: async (
		status: EOrderStatus[] | undefined,
		dietType: EOrderDietType[] | undefined,
		dietID: string[] | undefined,
		clientID: number | undefined,
		search: string | undefined,
		page: number = 1,
		perPage: number = 10,
	) => {
		const { data, meta } = await apiCommon<IOrder[]>(`${groupPrefix}`, {
			method: 'GET',
			...createQuery(
				{ page, perPage },
				{ search, status, client_id: clientID, diet_type: dietType, diet_id: dietID },
			),
		})
		return { data: data.map(d => Order(d)), meta }
	},

	fetchOrder: async (orderID: string) => {
		const { data } = await apiCommon<IOrder>(`${groupPrefix}/${orderID}`, {
			method: 'GET',
		})
		return Order(data)
	},

	fetchOrderOperations: async (orderID: string) => {
		const { data } = await apiCommon<IOrderOperation[]>(`${groupPrefix}/${orderID}/operations`, {
			method: 'GET',
		})
		return { data: data.map(d => OrderOperation(d)) }
	},

	cancelOrders: async (ids: number[]) => {
		const { data } = await apiCommon<IOrder[]>(`${groupPrefix}/cancel`, {
			method: 'POST',
			body: { client_diet_ids: ids },
		})
		return data
	},

	fetchOrderAddresses: async (orderID: string) => {
		const { data } = await apiCommon<IClientAddress[]>(`${groupPrefix}/${orderID}/addresses`, { method: 'GET' })
		return data ? data.map(d => ClientAddress(d)) : []
	},

	calculateOrderDaysChangeDiet: async (orderID: number, dietID: number, kcal: number, days: string[]) => {
		const { data } = await apiCommon<IOrderDayChange>(`${groupPrefix}/${orderID}/change-diet/calculate`, {
			method: 'POST',
			body: {
				kcal,
				diet_id: dietID,
				client_diet_days: days,
			},
		})
		return OrderDayChange(data)
	},

	calculateOrderDaysChangeAddress: async (orderID: number, addressID: number, days: string[]) => {
		const { data } = await apiCommon<IOrderDayChange>(`${groupPrefix}/${orderID}/change-address/calculate`, {
			method: 'POST',
			body: {
				address_id: addressID,
				client_diet_days: days,
			},
		})
		return OrderDayChange(data)
	},

	updateOrderDayDiet: async (
		orderID: number,
		dietID: number,
		kcal: number,
		obj: IOrderDayChangeModel,
		paymentType: EEmployeePaymentType,
	) => {
		const { data } = await apiCommon<IOrderDay[]>(`${groupPrefix}/${orderID}/change-diet`, {
			method: 'POST',
			body: {
				diet_id: dietID,
				kcal,
				employee_payment_type: paymentType,
				...orderDayChangeToJSON(obj),
			},
		})
		return { data: data.map(OrderDay) }
	},

	updateOrderDayAddress: async (
		clientDietID: number,
		addressID: number,
		obj: IOrderDayChangeModel,
		paymentType: EEmployeePaymentType = EEmployeePaymentType.PAID,
	) => {
		const { data } = await apiCommon<IOrderDay[]>(`${groupPrefix}/${clientDietID}/change-address`, {
			method: 'POST',
			body: { address_id: addressID, employee_payment_type: paymentType, ...orderDayChangeToJSON(obj) },
		})
		return { data: data.map(OrderDay) }
	},

	moveOrderDays: async (clientDietID: number, daysFrom: string[], daysTo: Date[]) => {
		const { data } = await apiCommon<IOrderDay[]>(`${groupPrefix}/${clientDietID}/move-days`, {
			method: 'POST',
			body: { dates: daysTo.map(dateToFilterFormat), client_diet_days: daysFrom },
		})
		return { data: data.map(OrderDay) }
	},

	calculateOrderDayCancel: async (orderID: number, day: string) => {
		const { data } = await apiCommon<IOrderDayChange>(`${groupPrefix}/${orderID}/cancel/calculate`, {
			method: 'POST',
			body: {
				client_diet_days: [day],
			},
		})
		return OrderDayChange(data)
	},

	cancelOrderDay: async (
		orderID: number,
		obj: IOrderDayChangeModel,
		paymentType: EEmployeePaymentType.IGNORE_POINTS | EEmployeePaymentType.REFUND_POINTS,
	) => {
		const { data } = await apiCommon<IOrderDay[]>(`${groupPrefix}/${orderID}/cancel`, {
			method: 'POST',
			body: {
				employee_payment_type: paymentType,
				...orderDayChangeToJSON(obj),
			},
		})
		return { data: data.map(OrderDay) }
	},

	calculateOrderDayAddon: async (orderID: number, addonID: number) => {
		const { data } = await apiCommon<IOrderDayChange>(`${groupPrefix}/${orderID}/addon/calculate`, {
			method: 'POST',
			body: {
				addons_id: [addonID],
			},
		})
		return OrderDayChange(data)
	},

	addOrderDayNewMeal: async (
		orderID: number,
		days: { date: Date | string; addonIDs: number[] | null; mealSlots: IOrderDaysMealFormModel[] | null }[],
	) => {
		const { data } = await apiCommon<IOrderDay[]>(`${groupPrefix}/${orderID}/extra-meals`, {
			method: 'POST',
			body: {
				diet_order_days: days.map(d => ({
					date: formatDateToInput(d.date),
					addons: d.addonIDs,
					meal_slots: d.mealSlots ? d.mealSlots.map(orderDayMealToJSON) : null,
				})),
			},
		})
		return { data: data.map(OrderDay) }
	},
}
